import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { CsrfService } from './csrf.service';

/** One checklist row of the Quick Start Guide panel. */
export interface GuideSubtask {
  code: string;
  /** Rendered verbatim; the wording is the design's, not the panel's. */
  label: string;
  completed: boolean;
  /** Panel route this row deep-links to. */
  path: string;
}

/**
 * The automation profile the guide is scoring its per-profile rows against.
 *
 * A tenant can hold several half-finished profiles, so the server names the one it
 * is watching. Absent once the task is completed: a finished task is never
 * re-evaluated, so there is no profile being tracked any more.
 */
export interface GuideTargetProfile {
  id: number;
  displayName: string;
  completeness: number;
}

/** One accordion group: the task header plus its subtask rows. */
export interface GuideTask {
  code: string;
  title: string;
  /**
   * Achieved, and therefore frozen. The Pending / Completed tabs and the top
   * progress bar count tasks on this flag.
   */
  completed: boolean;
  completedSubtasks: number;
  totalSubtasks: number;
  targetProfile: GuideTargetProfile | null;
  subtasks: GuideSubtask[];
}

export interface GuideResult {
  tasks: GuideTask[];
}

/**
 * HTTP client for the tenant Quick Start Guide.
 *
 * Holds no state and caches nothing: the panel refetches each time it opens, which
 * is what makes a completed wizard show up without a reload. Every completion count
 * arrives computed — the client never derives a checkmark, because the server is the
 * only place that knows whether a task has already been stamped.
 */
@Injectable({ providedIn: 'root' })
export class QuickStartService {
  private readonly http = inject(HttpClient);
  private readonly csrf = inject(CsrfService);
  private readonly auth = inject(AuthService);

  private getHeaders() {
    const headers: Record<string, string> = {};
    const token = this.csrf.getToken();
    if (token) {
      headers['x-csrf-token'] = token;
    }
    return headers;
  }

  /**
   * GET /api/v1/tenant/guide
   *
   * Reading the guide is what stamps a newly-achieved task server-side, so this is
   * not a pure read; it is however idempotent, and it never rewrites a stamp that
   * already exists.
   */
  get(): Observable<GuideResult> {
    return this.http.get<GuideResult>(
      `${environment.apiUrl}/api/v1/tenant/guide`,
      { withCredentials: true, headers: this.getHeaders() },
    ).pipe(this.auth.catchUnauthorized());
  }
}
